import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Search, X } from "lucide-react";
import cn from "classnames";
import { Card, CardDescription, CardTitle, Input } from "campaign-builder/ui";
import { apiClient } from "module/api";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useCampaignCostPreviewCalculation } from "campaign-builder/hooks/useCampaignCostPreviewCalculation";
import { AudienceStatus, type Audience } from "campaign-builder/domain/types";
import { useSaveStatus, trackSave } from "../saveStatus";
import { useTargetingValidation } from "../targetingValidation";
import { formatMinRecipientsMessage, useAdmCapabilities } from "../admCountryCapabilities";
import { FieldError } from "./fields";
import { AudienceSourceTiles } from "./AudienceSourceTiles";
import { InvalidationModal } from "campaign-builder/invalidation";
import { useTrackingInvalidation } from "../useTrackingInvalidation";

const PAGE_SIZE = 3;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

const formatCost = (value: number) =>
  `+ $${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Checkbox = ({ checked }: { checked: boolean }) => (
  <span
    className={cn(
      "flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border",
      checked ? "border-[#ef2b55] bg-[#ef2b55]" : "border-slate-300 dark:border-accent bg-background"
    )}
  >
    {checked && <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />}
  </span>
);

export const AudienceCard = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const {
    campaignData,
    audiences,
    audiencesLoading,
    campaignAudiences,
    recipientsAmount,
    refetchCampaignAudiences,
    loadMoreAudiences,
    hasMoreAudiences,
    isLoadingMoreAudiences,
  } = useCampaignData();
  const { minRecipients } = useAdmCapabilities();
  const { costPerFlyer } = useCampaignCostPreviewCalculation({ format: campaignData?.flyerType });
  const { hasPlaceholderTracking, resetTracking } = useTrackingInvalidation();
  const save = useSaveStatus();
  const { version: validationVersion } = useTargetingValidation();
  const [pendingToggle, setPendingToggle] = useState<Audience | null>(null);

  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [validationRequested, setValidationRequested] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (validationVersion > 0) setValidationRequested(true);
  }, [validationVersion]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const modal = modalRef.current;
    modal?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModalOpen(false);
        return;
      }
      if (event.key !== "Tab" || !modal) return;
      const focusables = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  const selectedIds = new Set(campaignAudiences.map((ca) => Number(ca.audience_id)));
  const hasAudiences = !audiencesLoading && audiences.some((a) => !a.deletedAt);

  const filtered = audiences
    .filter((a) => !a.deletedAt && a.name.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => Number(selectedIds.has(Number(b.id))) - Number(selectedIds.has(Number(a.id))));
  const visible = query ? filtered : filtered.slice(0, visibleCount);
  const canLoadMore = !query && (filtered.length > visibleCount || hasMoreAudiences);

  const handleLoadMore = () => {
    setVisibleCount((count) => count + PAGE_SIZE);
    if (hasMoreAudiences && visibleCount + PAGE_SIZE >= filtered.length) loadMoreAudiences();
  };

  const applyToggle = async (audience: Audience) => {
    if (!campaignId) return;
    setBusyId(audience.id);
    try {
      await trackSave(save, async () => {
        if (selectedIds.has(Number(audience.id))) {
          await apiClient.deleteCampaignAudience({ audience_id: audience.id, campaign_id: campaignId });
        } else {
          await apiClient.createCampaignAudience({ audience_id: audience.id, campaign_id: campaignId });
        }
        await refetchCampaignAudiences();
      });
    } catch (error) {
      // status reported by trackSave
    } finally {
      setBusyId(null);
    }
  };

  const toggle = async (audience: Audience) => {
    if (!campaignId || busyId) return;
    // Any audience change invalidates placeholder/file-based tracking (the variable
    // file is tied to the audience size) — warn first, clearing tracking on confirm (F5).
    if (hasPlaceholderTracking) {
      setPendingToggle(audience);
      return;
    }
    await applyToggle(audience);
  };

  const confirmToggle = async () => {
    const audience = pendingToggle;
    setPendingToggle(null);
    if (!audience) return;
    try {
      await resetTracking();
      await applyToggle(audience);
    } catch (error) {
      // reset failed — leave tracking intact and don't toggle the audience
    }
  };

  const audienceError = validationRequested
    ? campaignAudiences.length === 0
      ? "Select at least one audience to continue."
      : (recipientsAmount ?? 0) < minRecipients
      ? formatMinRecipientsMessage(minRecipients)
      : null
    : null;

  return (
    <Card className="flex flex-col gap-6 p-[25px]" data-targeting-section="audience">
      <div className="flex flex-col gap-1">
        <CardTitle>Audience</CardTitle>
        <CardDescription>
          {hasAudiences
            ? "Select one or more audiences for your campaign targeting or create a new audience."
            : "Choose how you'd like to configure your audience for this campaign."}
        </CardDescription>
        <FieldError message={audienceError} id="admAudienceError" />
      </div>

      {audiencesLoading ? (
        <p className="text-muted-foreground text-sm">Loading audiences…</p>
      ) : hasAudiences ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="text-slate-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by audience name"
                className="pl-9"
                data-cy="admAudienceSearchInput"
              />
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="text-foreground h-9 shrink-0 rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium"
              data-cy="admCreateAudienceButton"
            >
              Create new audience
            </button>
          </div>

          <div className="flex flex-col gap-2" aria-describedby={audienceError ? "admAudienceError" : undefined}>
            {visible.map((a) => {
              const checked = selectedIds.has(Number(a.id));
              const selectable = a.status === AudienceStatus.READY;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  // Stable accessible name: the row's visible text changes with
                  // selection/price, and Copilot control mode refuses a click
                  // whose label moved (MISMATCH) — which made it re-click and
                  // toggle the audience back off. State lives in aria-checked.
                  aria-label={a.name}
                  onClick={() => selectable && toggle(a)}
                  disabled={!selectable || busyId === a.id}
                  data-cy={`admAudienceOption-${a.id}`}
                  className={cn(
                    "flex items-center gap-3 rounded-[10px] border-solid p-4 text-left",
                    checked ? "border-[1.5px] border-[#222336] dark:border-slate-300" : "border border-border",
                    !selectable && "cursor-not-allowed opacity-50"
                  )}
                >
                  <Checkbox checked={checked} />
                  <span className="flex flex-1 flex-col">
                    <span className="text-foreground text-sm font-semibold leading-5">{a.name}</span>
                    <span className="text-muted-foreground text-xs leading-4">
                      {a.metadata.rowsAmount.toLocaleString("en-US")} recipients
                    </span>
                  </span>
                  <span className="text-foreground text-sm font-medium">
                    {formatCost(a.metadata.rowsAmount * costPerFlyer)}
                  </span>
                </button>
              );
            })}
            {visible.length === 0 && <p className="text-muted-foreground text-sm">No audiences match your search.</p>}
          </div>

          {canLoadMore && (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLoadingMoreAudiences}
              className="text-foreground mx-auto rounded-lg border border-solid border-border bg-background px-4 py-2 text-sm font-medium"
              data-cy="admAudienceLoadMoreButton"
            >
              {isLoadingMoreAudiences ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      ) : (
        <AudienceSourceTiles />
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onMouseDown={() => setModalOpen(false)}>
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-audience-title"
            tabIndex={-1}
            className="w-[560px] max-w-[90vw] rounded-xl bg-card p-6 shadow-[0px_10px_30px_rgba(2,6,23,0.18)] outline-none"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <span id="create-audience-title" className="text-[20px] font-medium text-foreground">
                Create new audience
              </span>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                data-cy="admCreateAudienceModalCloseButton"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <AudienceSourceTiles />
          </div>
        </div>
      )}
      <InvalidationModal
        variant={pendingToggle ? "audience" : null}
        onCancel={() => setPendingToggle(null)}
        onConfirm={() => void confirmToggle()}
      />
    </Card>
  );
};
